/**
 * Application state, backed by Supabase.
 *
 * Drop-in replacement for state/AppState.jsx. The context value exposes exactly
 * the same keys, so Shell, Dashboard, MyWork, People, Settings, Reports and
 * AuthPage keep working with no JSX changes.
 *
 * Two behavioural changes worth knowing:
 *
 * 1. AUTH IS NOW SUPABASE AUTH. The hand-rolled scrypt + HS256 in
 *    server/src/auth.js is gone. This is not cosmetic: RLS policies read
 *    auth.uid() from the `sub` claim of a Supabase-signed JWT, so tokens minted
 *    by our own Express server would leave every policy evaluating against null
 *    and every query returning nothing.
 *
 * 2. NOTIFICATIONS NO LONGER POLL. The 45-second setInterval is replaced by a
 *    private Realtime channel on 'user:<id>'. At 100 users the old poll was
 *    ~133 requests/minute of mostly-empty responses.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { supabase, unwrap } from '../lib/supabase.js';
import { navigate } from '../lib/router.js';
import { pushCapability, subscribeToPush } from '../lib/push.js';
import {
  isNativeApp, subscribeNativePush, watchNativeToken, onNativePushTap, setAppBadge,
} from '../lib/native-push.js';
import { pickWorkspace } from '../lib/workspace.js';

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const WORKSPACE_KEY = 'pulse.workspace';
const THEME_KEY = 'pulse.theme';

/**
 * Points the native status bar at the current theme.
 *
 * IMPORTED LAZILY, and only on a device. @capacitor/status-bar is a native
 * plugin: on the web it is dead weight in the bundle and its calls throw
 * "not implemented", so the import itself is deferred behind the platform
 * check rather than sitting at the top of the file.
 *
 * Every failure here is swallowed. A status bar that stayed the wrong colour
 * is a blemish; an unhandled rejection during a theme toggle is a bug, and the
 * two are not worth trading.
 */
async function syncStatusBar(theme) {
  const { Capacitor } = await import('@capacitor/core');
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    /* THE STYLE NAMES THE BACKGROUND, NOT THE TEXT. From the plugin's own
       docs: Dark is "light text for dark backgrounds", Light is "dark text for
       light backgrounds". Read as if it named the text - which is the obvious
       reading, and the one taken here first - the mapping inverts and both
       themes end up invisible: white text on the white top bar in light mode,
       dark text on the dark one in dark mode. The simulator showed exactly
       that, with the clock and battery simply absent. */
    await StatusBar.setStyle({ style: theme === 'dark' ? Style.Dark : Style.Light });
  } catch {
    /* Plugin missing or refused: leave the bar as the OS drew it. */
  }
}

/** Shapes a Supabase auth user + profile row into the object the UI renders. */
const toUser = (profile) => (profile ? {
  id: profile.id,
  email: profile.email,
  name: profile.name,
  title: profile.title,
  avatar_color: profile.avatar_color,
  avatar_url: profile.avatar_url,
  created_at: profile.created_at,
  handle: profile.handle ?? null,
  handle_locked: profile.handle_locked ?? false,
  availability: profile.availability ?? null,
  availability_message: profile.availability_message ?? null,
} : null);

/* Matches the check constraint in 0045. Two lines at the width the notice is
   drawn, and the database refuses anything longer. */
export const STATUS_MESSAGE_MAX = 140;

/** After this much silence the presence payload flips to idle ("Away"). */
const IDLE_AFTER_MS = 5 * 60_000;

/**
 * Shapes a notifications row into what the bell renders.
 *
 * Postgres columns are snake_case and the UI reads camelCase. Spreading the raw
 * row left notification.boardId, notification.itemId and notification.createdAt
 * undefined, so clicking an alert navigated nowhere at all - the handler's
 * `if (notification.itemId && notification.boardId)` was never true. The
 * desktop-notification path read row.board_id directly and did work, which is
 * why only the in-app bell was dead.
 */
const toNotification = (row) => ({
  ...row,
  boardId: row.board_id,
  itemId: row.item_id,
  updateId: row.update_id ?? null,
  conversationId: row.conversation_id ?? null,
  noteId: row.note_id ?? null,
  workspaceId: row.workspace_id ?? null,
  actorId: row.actor_id,
  createdAt: row.created_at,
  read: !!row.read_at,
});

/**
 * Where an alert should take you. Deepest target the row knows about: the
 * comment if it names one, otherwise the item, otherwise the board.
 */
export function notificationPath(notification) {
  const { boardId, itemId, updateId, conversationId, noteId } = notification;
  // A direct message or a note has no board at all, so these come first or
  // every one of them would return null and the alert would not be clickable.
  if (noteId) return `/notes/${noteId}`;
  if (conversationId) return `/chats/${conversationId}`;
  if (!boardId) return null;
  if (!itemId) return `/boards/${boardId}`;
  const query = updateId ? `?item=${itemId}&update=${updateId}` : `?item=${itemId}`;
  return `/boards/${boardId}${query}`;
}

export function AppProvider({ children }) {
  const [booting, setBooting] = useState(true);
  const [bootError, setBootError] = useState(null);
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState(() => localStorage.getItem(WORKSPACE_KEY) || null);
  /*
   * The same value, readable from a callback that cannot see state.
   *
   * loadWorkspaces is memoised with no dependencies - it has to be, because
   * onAuthStateChange depends on it - so it cannot read workspaceId. It used to
   * fall back to localStorage instead, which is SHARED BETWEEN TABS: a second
   * tab switching workspace rewrote the key, and the next auth event or invite
   * in this tab silently moved the user to that other workspace. Everything
   * they did next - invites above all - was written against it.
   */
  const workspaceIdRef = useRef(workspaceId);
  const selectWorkspace = useCallback((id) => {
    workspaceIdRef.current = id;
    setWorkspaceId(id);
    if (id) localStorage.setItem(WORKSPACE_KEY, id);
  }, []);
  const [boards, setBoards] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  /* The app icon follows the same number the bell shows. Push sets it while
     the app is closed (0048); this is what walks it back down as things are
     read, which no payload can do because none is sent. */
  useEffect(() => { setAppBadge(unread); }, [unread]);
  // { boardId: count } for the sidebar badges. Only boards with something
  // unread appear, so an absent key is zero.
  const [unreadByBoard, setUnreadByBoard] = useState({});
  // True between clicking a password-reset link and choosing a new password.
  // The link signs you in, so without this the app would simply drop you on
  // the dashboard and never ask for the new password you came to set.
  const [recovering, setRecovering] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [theme, setThemeState] = useState(() => document.documentElement.dataset.theme || 'light');
  // Push preferences. `null` while unloaded, so the UI can tell "off" from
  // "not known yet" and avoid flashing a toggle into the wrong position.
  const [pushPrefs, setPushPrefs] = useState(null);
  // Only boards with an explicit override appear here. Absent means on, which
  // is what makes a newly created board behave sensibly with no row.
  const [boardPushOff, setBoardPushOff] = useState(() => new Set());

  const toastId = useRef(0);

  /* ------------------------------------------------------------------ toasts */

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message, tone = 'info') => {
    toastId.current += 1;
    const id = toastId.current;
    setToasts((current) => [...current, { id, message, tone }]);
    setTimeout(() => dismissToast(id), tone === 'error' ? 6000 : 3600);
  }, [dismissToast]);

  const guard = useCallback(async (fn, { silent = false } = {}) => {
    try {
      return await fn();
    } catch (error) {
      if (!silent) toast(error.message || 'Something went wrong', 'error');
      return null;
    }
  }, [toast]);

  /* ------------------------------------------------------------------- theme */

  const setTheme = useCallback((next) => {
    document.documentElement.dataset.theme = next;
    localStorage.setItem(THEME_KEY, next);
    setThemeState(next);
    /* The native status bar is not part of the web view and does not follow the
       page. Left alone it draws dark text in both themes, which on Pulse's dark
       top bar is dark on dark - the clock and the battery simply disappear.
       Style names read backwards on purpose: Style.Dark means dark CONTENT, so
       the light theme wants Dark and vice versa. */
    syncStatusBar(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark');
  }, [setTheme]);

  /* On launch too, not only on a toggle. The theme is restored from storage
     before any of this runs, so without this the status bar keeps whatever the
     OS chose until the user happens to switch themes - which on a dark install
     means an invisible clock for the entire first session. */
  useEffect(() => { syncStatusBar(theme); }, [theme]);

  /* ----------------------------------------------------------------- session */

  const loadWorkspaces = useCallback(async (preferredId) => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return null;

    // Read through workspace_members rather than workspaces directly, because
    // the caller's own role rides along with it. People.jsx and Settings.jsx
    // gate their admin controls on workspace.role, so dropping it here would
    // silently hide the manage-team UI from owners.
    // The nested workspace_members(count) is what feeds the sidebar's
    // "N members" line. The Express /workspaces route returned memberCount and
    // the first Supabase version dropped it, so the sidebar read
    // "undefined members".
    const SELECT = 'role, joined_at, workspace:workspace_id (*, workspace_members(count))';

    let rows = unwrap(await supabase
      .from('workspace_members')
      .select(SELECT)
      .eq('user_id', authUser.id));

    // An account with no workspace cannot render anything: Dashboard returns
    // early on a null workspaceId and spins forever. bootstrap_account is
    // idempotent, so this both covers a signup whose bootstrap call failed and
    // repairs accounts created before that RPC existed.
    if (rows.length === 0) {
      await supabase.rpc('bootstrap_account');
      rows = unwrap(await supabase
        .from('workspace_members')
        .select(SELECT)
        .eq('user_id', authUser.id));
    }

    const list = rows
      .filter((r) => r.workspace)
      .map((r) => ({
        ...r.workspace,
        role: r.role,
        joinedAt: r.joined_at,
        memberCount: r.workspace.workspace_members?.[0]?.count ?? 0,
      }))
      .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));

    setWorkspaces(list);
    const chosen = pickWorkspace(list, {
      preferred: preferredId,
      current: workspaceIdRef.current,
      stored: localStorage.getItem(WORKSPACE_KEY),
    });
    if (chosen) selectWorkspace(chosen.id);
    return chosen;
  }, [selectWorkspace]);

  /**
   * The signed-in user's profile row, self-healing if it is missing.
   *
   * .single() raises "Cannot coerce the result to a single JSON object" when it
   * matches no rows. That threw out of the onAuthStateChange callback, so
   * setBooting(false) never ran and the app rendered nothing at all - a blank
   * page with no error and no way to sign out.
   *
   * A session can legitimately outlive its profile row: handle_new_user only
   * fires on insert into auth.users, so anything that removes the profile
   * afterwards leaves an account that can authenticate but not load. Rather than
   * fail, recreate it from the auth user's own metadata.
   */
  const loadProfile = useCallback(async (authUser) => {
    if (!authUser) return null;

    const existing = unwrap(await supabase
      .from('profiles')
      .select('*')
      .eq('id', authUser.id)
      .maybeSingle());
    if (existing) return toUser(existing);

    const meta = authUser.user_metadata || {};
    const repaired = unwrap(await supabase
      .from('profiles')
      .upsert({
        id: authUser.id,
        email: authUser.email,
        name: meta.name || String(authUser.email || '').split('@')[0],
        avatar_color: meta.avatar_color || '#2a78d6',
      }, { onConflict: 'id' })
      .select()
      .maybeSingle());

    return toUser(repaired);
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setWorkspaces([]);
    // Otherwise the next account to sign in on this browser starts by asking
    // for a workspace it has no business being in.
    workspaceIdRef.current = null;
    setBoards([]);
    setNotifications([]);
    setUnread(0);
    navigate('/login');
  }, []);

  // Replaces the onUnauthorized() listener: supabase-js refreshes tokens on its
  // own and emits SIGNED_OUT only once refresh has actually failed.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
        setRecovering(false);
        setBooting(false);
        return;
      }
      // Arriving from a reset link. supabase-js has already consumed the token
      // in the URL and signed them in, so without this flag they would land on
      // the dashboard and never be asked for the password they came to set.
      if (event === 'PASSWORD_RECOVERY') setRecovering(true);
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'PASSWORD_RECOVERY') {
        // try/finally, not a bare await chain: anything that throws in here used
        // to skip setBooting(false) and leave the app stuck rendering nothing.
        // Booting must end whether or not the profile and workspaces loaded.
        try {
          const me = await loadProfile(session.user);
          setUser(me);
          if (me) await guard(() => loadWorkspaces(), { silent: true });
        } catch (err) {
          setUser(null);
          setBootError(err.message || 'Could not load your account');
        } finally {
          setBooting(false);
        }
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [loadProfile, loadWorkspaces, guard]);

  /**
   * Sets a new password and ends the recovery state.
   *
   * updateUser authorises on the recovery session alone, with no old password
   * to supply - which is the whole point of a reset link, and why the link is
   * single-use and short-lived.
   */
  const completePasswordReset = useCallback(async (password) => {
    unwrap(await supabase.auth.updateUser({ password }));
    setRecovering(false);
    return true;
  }, []);

  /** Redeems an invite token. Safe to call when already a member. */
  const acceptInvite = useCallback(async (token) => {
    const workspaceId = unwrap(await supabase.rpc('accept_invite', { p_token: token }));
    await loadWorkspaces(workspaceId);
    return workspaceId;
  }, [loadWorkspaces]);

  const signIn = useCallback(async (credentials) => {
    const data = unwrap(await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    }));
    const me = await loadProfile(data.user);
    setUser(me);

    // Redeem before loadWorkspaces, not after. loadWorkspaces bootstraps a
    // personal workspace when it finds none, so accepting afterwards would
    // leave an invited user in two workspaces and land them on their empty one.
    if (credentials.inviteToken) {
      await guard(() => acceptInvite(credentials.inviteToken));
    }

    await loadWorkspaces();
    navigate('/');
    return me;
  }, [loadProfile, loadWorkspaces, acceptInvite, guard]);

  const signUp = useCallback(async (details) => {
    const data = unwrap(await supabase.auth.signUp({
      email: details.email,
      password: details.password,
      // handle_new_user() reads this to populate public.profiles.
      options: { data: { name: details.name, avatar_color: details.avatar_color } },
    }));

    // With email confirmation on, there is no session yet and nothing to load.
    if (!data.session) {
      toast('Check your email to confirm your account', 'info');
      return null;
    }

    const me = await loadProfile(data.user);
    setUser(me);

    // AuthPage passes the token from the #/invite/:token route. Reading
    // window.location.search instead was the bug: invite links are hash routes,
    // so the query string is always empty, accept_invite never ran, and the
    // invitee silently got a brand-new workspace of their own while the invite
    // stayed "pending" forever.
    const token = details.inviteToken || null;
    let joined = false;
    if (token) {
      joined = Boolean(await guard(async () => {
        unwrap(await supabase.rpc('accept_invite', { p_token: token }));
        return true;
      }));
    }
    // Only bootstrap a personal workspace when they did not join one. Doing it
    // unconditionally would leave an invited user in two workspaces, landing on
    // their empty one.
    if (!joined) {
      await guard(async () => unwrap(await supabase.rpc('bootstrap_account')));
    }

    await loadWorkspaces();
    navigate('/');
    return me;
  }, [loadProfile, loadWorkspaces, guard]);

  /* ---------------------------------------------------------- workspace data */

  const refreshBoards = useCallback(async () => {
    if (!workspaceId) return [];
    const rows = unwrap(await supabase
      .from('boards')
      // Nested counts replace the per-board COUNT(*) the Express route ran in a
      // loop: one request instead of 1 + 2N.
      .select('*, items(count), groups(count)')
      .eq('workspace_id', workspaceId)
      .eq('archived', false)
      .order('position'));

    const shaped = rows.map((b) => ({
      ...b,
      itemCount: b.items?.[0]?.count ?? 0,
      groupCount: b.groups?.[0]?.count ?? 0,
    }));
    setBoards(shaped);
    return shaped;
  }, [workspaceId]);

  const refreshDirectory = useCallback(async () => {
    if (!workspaceId) return;
    const rows = unwrap(await supabase
      .from('workspace_members')
      .select('role, joined_at, profiles(*)')
      .eq('workspace_id', workspaceId));

    setDirectory(rows.map((r) => ({ ...r.profiles, role: r.role, joined_at: r.joined_at })));
  }, [workspaceId]);

  useEffect(() => {
    if (!user || !workspaceId) return;
    guard(() => Promise.all([refreshBoards(), refreshDirectory()]), { silent: true });
  }, [user, workspaceId, refreshBoards, refreshDirectory, guard]);

  /* `to` is for opening a board that lives in another workspace: switching and
     then landing on the dashboard would make the user find it a second time. */
  const switchWorkspace = useCallback((id, to = '/') => {
    selectWorkspace(id);
    setBoards([]);
    /* Land where you were, not at the lobby: every workspace remembers its
       last-opened board (BoardPage records it) and switching returns there.
       A remembered board that has since been deleted falls back cleanly -
       BoardPage clears the key when a board fails to load. */
    const remembered = to === '/' && localStorage.getItem(`pulse.lastBoard.${id}`);
    navigate(remembered ? `/boards/${remembered}` : to);
  }, [selectWorkspace]);

  /* ----------------------------------------------------------- notifications */

  const refreshNotifications = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    try {
      // The counts come from their own aggregate rather than from the 40 rows
      // the bell shows: a board with 60 unread would otherwise report 40.
      const [rows, counts] = await Promise.all([
        supabase.from('notifications').select('*')
          .order('created_at', { ascending: false }).limit(40),
        supabase.rpc('unread_by_board'),
      ]);
      if (rows.data) {
        setNotifications(rows.data.map(toNotification));
        setUnread(rows.data.filter((n) => !n.read_at).length);
      }
      if (counts.data) setUnreadByBoard(counts.data);
    } catch {
      // A failed refresh is not worth interrupting the user for.
    }
  }, []);

  /* Board badges are cleared one notification at a time, from the bell.
     markBoardRead used to run on every board open, which meant walking past a
     board with seven alerts destroyed all seven without showing you one of
     them. mark_board_read still exists in the database, unused. */

  /* ------------------------------------------------------------------- push */

  /**
   * Keeps this device's push subscription current, silently.
   *
   * Only when permission is ALREADY granted, on either platform. Re-subscribing
   * is how a rotated endpoint or a cleared subscription heals itself, but asking
   * for permission needs a click - Settings owns that - and an unprompted
   * permission dialog on load is the surest way to be denied for good.
   *
   * The app takes the native branch and never the web one: inside a WebView
   * pushCapability() reports unsupported anyway, because there is no
   * PushManager to find, so the guard below is belt and braces rather than the
   * thing doing the work.
   */
  useEffect(() => {
    if (!user) return undefined;

    if (isNativeApp()) {
      let stopWatching = () => {};
      // subscribeNativePush only prompts when the OS has never been asked; on
      // a granted install it is a token refresh, which is exactly what this
      // effect is for. On a denied one it throws and is swallowed below.
      subscribeNativePush(user.id)
        .then(() => watchNativeToken(user.id))
        .then((stop) => { stopWatching = stop; })
        .catch((error) => {
          console.warn('[push] native registration failed', error.message);
        });
      return () => stopWatching();
    }

    const { supported, configured, permission } = pushCapability();
    if (!supported || !configured || permission !== 'granted') return undefined;
    subscribeToPush(user.id).catch((error) => {
      // Nothing actionable for the user here: they asked for none of this, and
      // Settings will show the real state next time they look.
      console.warn('[push] could not refresh the subscription', error.message);
    });
    return undefined;
  }, [user]);

  /**
   * A tap on a notification opens what it was announcing.
   *
   * The browser equivalent lives in the service worker's notificationclick
   * handler; an app has no service worker, so the routing has to happen in the
   * page. Both read the same `url` off the payload, so the two cannot drift.
   *
   * NOT SCOPED TO user: the listener has to be attached before the tap that
   * launched the app is replayed, and waiting for a session to load would miss
   * a cold start. navigate() into a signed-out app lands on the auth screen,
   * which is the right answer anyway.
   */
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    let stop = () => {};
    onNativePushTap((url) => navigate(url)).then((remove) => { stop = remove; });
    return () => stop();
  }, []);

  // Live, replacing the 45s poll. Private channel; RLS on realtime.messages
  // restricts 'user:<id>' to that user.
  useEffect(() => {
    if (!user) return undefined;
    refreshNotifications();

    const channel = supabase
      .channel(`user:${user.id}`, { config: { private: true } })
      .on('broadcast', { event: '*' }, ({ payload }) => {
        const row = payload?.record;
        if (!row) return;
        const incoming = toNotification(row);
        setNotifications((current) => {
          if (current.some((n) => n.id === row.id)) {
            return current.map((n) => (n.id === row.id ? incoming : n));
          }
          return [incoming, ...current].slice(0, 40);
        });
        setUnread((n) => (row.read_at ? n : n + 1));
        // Added to a workspace by an admin (0062): it belongs in the switcher now.
        if (row.kind === 'workspace_added') loadWorkspaces();
        if (row.board_id && !row.read_at) {
          setUnreadByBoard((current) => ({
            ...current, [row.board_id]: (current[row.board_id] || 0) + 1,
          }));
        }
        // No Notification is raised from here any more. The service worker
        // shows it, whether or not a tab is open, and doing both meant two
        // notifications for one event on every hidden tab.
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, refreshNotifications]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Reading the thing IS reading the notification. Opening an item's thread
   * or a chat marks every unread alert pointing at it - otherwise the bell
   * kept shouting about a comment you had already answered.
   *
   * One server update, then a refresh: the counters (unread, per-board) come
   * from aggregates over ALL rows, not the 40 the bell holds, so recounting
   * beats guessing.
   */
  const markSeenFor = useCallback(async ({ itemId, conversationId, noteId }) => {
    const column = itemId ? 'item_id' : noteId ? 'note_id' : 'conversation_id';
    const value = itemId || noteId || conversationId;
    if (!value) return;
    const any = notifications.some((n) => !n.read
      && (itemId ? n.itemId === itemId : noteId ? n.noteId === noteId : n.conversationId === conversationId));
    const ok = await guard(async () => unwrap(await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq(column, value)
      .is('read_at', null)
      .select('id')), { silent: true });
    // Refresh even when the visible 40 had no match: the aggregate counters
    // can know about rows the bell list does not.
    if (ok?.length || any) refreshNotifications();
  }, [guard, notifications, refreshNotifications]);

  const markNotificationRead = useCallback(async (id) => {
    const target = notifications.find((n) => n.id === id);
    setNotifications((current) => current.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((n) => Math.max(0, n - 1));
    if (target?.boardId && !target.read) {
      setUnreadByBoard((current) => {
        const left = (current[target.boardId] || 1) - 1;
        const next = { ...current };
        if (left > 0) next[target.boardId] = left; else delete next[target.boardId];
        return next;
      });
    }
    await guard(
      async () => unwrap(await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id)),
      { silent: true },
    );
  }, [guard, notifications]);

  /** Puts one back in the pile. The counters move the other way. */
  const markNotificationUnread = useCallback(async (id) => {
    const target = notifications.find((n) => n.id === id);
    if (!target || !target.read) return;
    setNotifications((current) => current.map((n) => (n.id === id ? { ...n, read: false } : n)));
    setUnread((n) => n + 1);
    if (target.boardId) {
      setUnreadByBoard((current) => ({
        ...current,
        [target.boardId]: (current[target.boardId] || 0) + 1,
      }));
    }
    await guard(
      async () => unwrap(await supabase.from('notifications').update({ read_at: null }).eq('id', id)),
      { silent: true },
    );
  }, [guard, notifications]);

  /** Removes it entirely. Optimistic; a failure restores the list. */
  const deleteNotification = useCallback(async (id) => {
    const target = notifications.find((n) => n.id === id);
    if (!target) return;
    setNotifications((current) => current.filter((n) => n.id !== id));
    if (!target.read) {
      setUnread((n) => Math.max(0, n - 1));
      if (target.boardId) {
        setUnreadByBoard((current) => {
          const left = (current[target.boardId] || 1) - 1;
          const next = { ...current };
          if (left > 0) next[target.boardId] = left; else delete next[target.boardId];
          return next;
        });
      }
    }
    const ok = await guard(
      async () => unwrap(await supabase.from('notifications').delete().eq('id', id)),
      { silent: true },
    );
    if (ok === null) refreshNotifications();
  }, [guard, notifications, refreshNotifications]);

  const markAllRead = useCallback(async () => {
    setNotifications((current) => current.map((n) => ({ ...n, read: true })));
    setUnread(0);
    setUnreadByBoard({});
    await guard(
      async () => unwrap(await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .is('read_at', null)),
      { silent: true },
    );
  }, [guard]);

  /* ------------------------------------------------------- push preferences */

  const loadPushPrefs = useCallback(async () => {
    const [prefs, boardPrefs] = await Promise.all([
      supabase.from('notification_prefs').select('*').maybeSingle(),
      supabase.from('board_notification_prefs').select('board_id, push_enabled'),
    ]);
    // A user who has never touched Settings has no row, and the defaults in
    // push_allowed say that means everything is on.
    setPushPrefs({
      pushEnabled: prefs.data ? prefs.data.push_enabled : true,
      mutedUntil: prefs.data?.muted_until || null,
      deadlineReminders: prefs.data ? prefs.data.deadline_reminders : true,
    });
    setBoardPushOff(new Set(
      (boardPrefs.data || []).filter((row) => !row.push_enabled).map((row) => row.board_id),
    ));
  }, []);

  useEffect(() => {
    if (!user) {
      setPushPrefs(null);
      setBoardPushOff(new Set());
      return;
    }
    loadPushPrefs();
  }, [user, loadPushPrefs]);

  /**
   * Writes the user's row, creating it if this is the first change.
   *
   * Optimistic, because a toggle that waits for a round trip before moving
   * feels broken; the reload on failure puts it back.
   */
  const savePushPrefs = useCallback(async (patch) => {
    if (!user) return false;
    const next = {
      ...(pushPrefs || { pushEnabled: true, mutedUntil: null, deadlineReminders: true }),
      ...patch,
    };
    setPushPrefs(next);
    const ok = await guard(async () => unwrap(await supabase
      .from('notification_prefs')
      .upsert({
        user_id: user.id,
        push_enabled: next.pushEnabled,
        muted_until: next.mutedUntil,
        deadline_reminders: next.deadlineReminders,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id' })));
    if (!ok) await loadPushPrefs();
    return !!ok;
  }, [user, pushPrefs, guard, loadPushPrefs]);

  const setPushEnabled = useCallback(
    (enabled) => savePushPrefs({ pushEnabled: enabled }),
    [savePushPrefs],
  );

  /* Read by scan_deadlines when it decides whether to write the row at all, so
     off means no bell entry either, not just no push. */
  const setDeadlineReminders = useCallback(
    (enabled) => savePushPrefs({ deadlineReminders: enabled }),
    [savePushPrefs],
  );

  /**
   * Mute for a window rather than until further notice. `hours` of 0 lifts it.
   * Stored as the moment it expires so nothing has to run to clear it.
   */
  const mutePushFor = useCallback((hours) => savePushPrefs({
    mutedUntil: hours > 0
      ? new Date(Date.now() + hours * 3600 * 1000).toISOString()
      : null,
  }), [savePushPrefs]);

  const setBoardPushEnabled = useCallback(async (boardId, enabled) => {
    if (!user) return false;
    setBoardPushOff((current) => {
      const next = new Set(current);
      if (enabled) next.delete(boardId); else next.add(boardId);
      return next;
    });
    // The row is kept either way rather than deleted when re-enabled: an
    // explicit "yes" and an absent row resolve the same, and keeping it means
    // one code path instead of an upsert-or-delete branch.
    const ok = await guard(async () => unwrap(await supabase
      .from('board_notification_prefs')
      .upsert({ user_id: user.id, board_id: boardId, push_enabled: enabled },
        { onConflict: 'user_id,board_id' })));
    if (!ok) await loadPushPrefs();
    return !!ok;
  }, [user, guard, loadPushPrefs]);

  /** Is the mute still in force? Recomputed on read; nothing polls for expiry. */
  const muteActive = !!(pushPrefs?.mutedUntil && new Date(pushPrefs.mutedUntil) > new Date());

  const boardPushEnabled = useCallback(
    (boardId) => !boardPushOff.has(boardId),
    [boardPushOff],
  );

  // The service worker cannot route the app, so a click on a notification in a
  // browser without Client.navigate() arrives here instead.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      if (event.data?.type === 'pulse-navigate' && event.data.path) navigate(event.data.path);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  const peopleById = useMemo(
    () => new Map(directory.map((person) => [person.id, person])),
    [directory],
  );

  /* ------------------------------------------------------------- presence */

  /* userId -> { state: 'active'|'idle', availability } for everyone with a tab
     open, via one workspace-wide Realtime presence topic. Live state only -
     the durable half ('busy', 'leave') is profiles.availability, and
     presenceOf() below folds the two together. */
  const [presence, setPresence] = useState(() => new Map());
  const presenceChannel = useRef(null);
  const availabilityRef = useRef(null);
  availabilityRef.current = user?.availability ?? null;
  /* Rides along with the status word for the same reason it does: someone who
     sets a message wants it seen now, not after everyone else's next reload.
     The directory row is still the fallback for anyone who was not listening. */
  const statusMessageRef = useRef(null);
  statusMessageRef.current = user?.availability_message ?? null;

  useEffect(() => {
    if (!user) return undefined;
    const channel = supabase.channel('online', {
      config: { private: true, presence: { key: user.id } },
    });
    const sync = () => {
      const state = channel.presenceState();
      setPresence(new Map(Object.entries(state)
        .map(([id, metas]) => [id, metas[metas.length - 1]])));
    };
    channel
      .on('presence', { event: 'sync' }, sync)
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          channel.track({
            state: 'active',
            availability: availabilityRef.current,
            availability_message: statusMessageRef.current,
          });
        }
      });
    presenceChannel.current = channel;
    return () => {
      presenceChannel.current = null;
      supabase.removeChannel(channel);
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Active vs idle, from input. Listeners are passive and the re-track fires
     only on the two TRANSITIONS, so mouse movement costs a timestamp write and
     nothing more. A hidden tab counts as idle immediately: "Available" should
     mean at the keyboard, not minimised. */
  useEffect(() => {
    if (!user) return undefined;
    let lastInput = Date.now();
    let idle = false;
    const retrack = () => presenceChannel.current?.track({
      state: idle ? 'idle' : 'active',
      availability: availabilityRef.current,
      availability_message: statusMessageRef.current,
    });
    const poke = () => {
      lastInput = Date.now();
      if (idle && !document.hidden) { idle = false; retrack(); }
    };
    const check = setInterval(() => {
      const shouldIdle = document.hidden || Date.now() - lastInput > IDLE_AFTER_MS;
      if (shouldIdle !== idle) { idle = shouldIdle; retrack(); }
    }, 30_000);
    const onVisibility = () => {
      if (document.hidden) { if (!idle) { idle = true; retrack(); } } else poke();
    };
    window.addEventListener('pointermove', poke, { passive: true });
    window.addEventListener('keydown', poke, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(check);
      window.removeEventListener('pointermove', poke);
      window.removeEventListener('keydown', poke);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * One person's effective status, resolved in precedence order:
   * declared leave > declared busy > live activity > offline.
   *
   * The declared half prefers the presence payload (fresh, no reload needed)
   * and falls back to the directory row - which is exactly the case that
   * matters for leave: the person's tab is closed, presence knows nothing,
   * and the profile still says where they are.
   */
  const presenceOf = useCallback((userId) => {
    if (!userId) return 'offline';
    const live = presence.get(userId);
    const declared = live?.availability !== undefined
      ? live.availability
      : peopleById.get(userId)?.availability;
    if (declared === 'leave') return 'leave';
    if (declared === 'busy') return 'busy';
    if (!live) return 'offline';
    return live.state === 'idle' ? 'away' : 'available';
  }, [presence, peopleById]);

  /**
   * The message someone left with their status, or null.
   *
   * Same two sources as presenceOf and the same precedence: the live payload
   * first because it needs no reload, the directory row behind it because that
   * is what survives the tab closing - which is the case that matters most
   * here, since "on leave until the 4th" is written precisely by someone who
   * is about to disappear.
   */
  const statusMessageOf = useCallback((userId) => {
    if (!userId) return null;
    const live = presence.get(userId);
    const message = live?.availability_message !== undefined
      ? live.availability_message
      : peopleById.get(userId)?.availability_message;
    return message || null;
  }, [presence, peopleById]);

  /** Sets the line that goes with my status. Empty clears it. */
  const setStatusMessage = useCallback(async (text) => {
    const next = text?.trim() ? text.trim().slice(0, STATUS_MESSAGE_MAX) : null;
    const ok = await guard(async () => unwrap(await supabase
      .from('profiles')
      .update({ availability_message: next })
      .eq('id', user.id)
      .select('id')
      .single()));
    if (ok === null) return;
    setUser((current) => (current ? { ...current, availability_message: next } : current));
    statusMessageRef.current = next;
    presenceChannel.current?.track({
      state: 'active',
      availability: availabilityRef.current,
      availability_message: next,
    });
    refreshDirectory();
  }, [user?.id, guard, refreshDirectory]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Sets my declared status: null (automatic), 'busy', or 'leave'. */
  const setAvailability = useCallback(async (next) => {
    const ok = await guard(async () => unwrap(await supabase
      .from('profiles')
      .update({ availability: next })
      .eq('id', user.id)
      .select('id')
      .single()));
    if (ok === null) return;
    setUser((current) => (current ? { ...current, availability: next } : current));
    availabilityRef.current = next;
    // Everyone with a tab open sees the change now; the directory row covers
    // everyone who loads later.
    presenceChannel.current?.track({ state: 'active', availability: next });
    refreshDirectory();
  }, [user?.id, guard, refreshDirectory]); // eslint-disable-line react-hooks/exhaustive-deps

  const workspace = useMemo(
    () => workspaces.find((w) => w.id === workspaceId) || null,
    [workspaces, workspaceId],
  );

  const value = useMemo(() => ({
    booting,
    bootError,
    user,
    setUser,
    signIn,
    signUp,
    signOut,
    workspaces,
    workspace,
    workspaceId,
    switchWorkspace,
    loadWorkspaces,
    acceptInvite,
    boards,
    setBoards,
    refreshBoards,
    directory,
    peopleById,
    refreshDirectory,
    presenceOf,
    setAvailability,
    statusMessageOf,
    setStatusMessage,
    recovering,
    completePasswordReset,
    notifications,
    unread,
    unreadByBoard,
    refreshNotifications,
    markNotificationRead,
    markSeenFor,
    markNotificationUnread,
    deleteNotification,
    markAllRead,

    toast,
    toasts,
    dismissToast,
    guard,
    theme,
    toggleTheme,
    pushPrefs,
    muteActive,
    loadPushPrefs,
    setPushEnabled,
    setDeadlineReminders,
    mutePushFor,
    boardPushEnabled,
    setBoardPushEnabled,
  }), [
    booting, bootError, user, signIn, signUp, signOut, workspaces, workspace, workspaceId, switchWorkspace,
    loadWorkspaces, acceptInvite,
    boards, refreshBoards, directory, peopleById, refreshDirectory, notifications,
    presenceOf, setAvailability, statusMessageOf, setStatusMessage,
    recovering, completePasswordReset,
    unread, unreadByBoard, refreshNotifications, markNotificationRead, markSeenFor,
    markNotificationUnread, deleteNotification, markAllRead,
    toast, toasts, dismissToast,
    guard, theme, toggleTheme,
    pushPrefs, muteActive, loadPushPrefs, setPushEnabled, setDeadlineReminders, mutePushFor,
    boardPushEnabled, setBoardPushEnabled,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
